import { TouchableOpacity, View, Text, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { Colors, ModernPalette } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';

interface SettingItemProps {
  icon?: keyof typeof Ionicons.glyphMap;
  iconColor?: string;
  label: string;
  value?: string;
  onPress?: () => void;
  showChevron?: boolean;
  textColor: string;
  isFirst?: boolean;
  isLast?: boolean;
  isDanger?: boolean;
}

/**
 * Setting Item Component
 * Reusable row component for individual settings.
 * Supports icons, values, press actions, and chevron indicators.
 */
export function SettingItem({
  icon,
  iconColor,
  label,
  value,
  onPress,
  showChevron = true,
  textColor,
  isFirst,
  isLast,
  isDanger,
}: SettingItemProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const backgroundColor = colorScheme === 'light' ? ModernPalette.white : palette.backgroundLight;

  const content = (
    <>
      {icon && (
        <View style={styles.iconContainer}>
          <Ionicons name={icon} size={22} color={iconColor || textColor} />
        </View>
      )}
      <Text style={[styles.label, { color: isDanger ? ModernPalette.danger : textColor, fontFamily: getBodyFont() }]}>{label}</Text>
      {value && <Text style={[styles.value, { color: palette.textMuted }]}>{value}</Text>}
      {showChevron && onPress && (
        <Ionicons name="chevron-forward" size={20} color={ModernPalette.textTertiary} />
      )}
    </>
  );

  const containerStyle = [
    styles.container,
    { backgroundColor },
    isFirst && styles.firstItem,
    isLast && styles.lastItem,
    !isLast && styles.withBorder,
  ];

  if (onPress) {
    return (
      <TouchableOpacity
        style={containerStyle}
        onPress={onPress}
        activeOpacity={0.6}>
        {content}
      </TouchableOpacity>
    );
  }

  return <View style={containerStyle}>{content}</View>;
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: Spacing.md + Spacing.xxs, // 14px
    paddingHorizontal: Spacing.lg,
    minHeight: 52,
  },
  firstItem: {
    borderTopLeftRadius: BorderRadius.md,
    borderTopRightRadius: BorderRadius.md,
  },
  lastItem: {
    borderBottomLeftRadius: BorderRadius.md,
    borderBottomRightRadius: BorderRadius.md,
  },
  withBorder: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ModernPalette.borderLight,
  },
  iconContainer: {
    width: 30,
    marginRight: Spacing.md,
    alignItems: 'center',
  },
  label: {
    flex: 1,
    fontSize: 14,
  },
  value: {
    fontSize: 14,
    marginRight: Spacing.sm,
  },
});
